import { Component, OnInit, HostListener } from '@angular/core';
import { PrinterService, PaperSize, ConnectionType, PrinterSettings, LabelProtocol, A5BillColumns, A5BillColumnKey, A4GstBillColumns, A4GstBillColumnKey, A4NonGstBillColumns, A4NonGstBillColumnKey } from '../../../core/services/printer.service';
import {
  PRODUCT_LABEL_GAP_MM,
  PRODUCT_LABEL_HEIGHT_MM,
  PRODUCT_LABEL_PITCH_DOTS,
  PRODUCT_LABEL_WIDTH_MM
} from '../../../core/services/label-spec';

/** A5 bill column options for the UI (key + label) */
export const A5_COLUMN_OPTIONS: { key: A5BillColumnKey; label: string }[] = [
  { key: 'sno', label: 'S.No' },
  { key: 'particulars', label: 'Particulars' },
  { key: 'mrp', label: 'MRP' },
  { key: 'qty', label: 'Qty' },
  { key: 'rateBeforeDisc', label: 'Rate (B Disc)' },
  { key: 'discount', label: 'Discount' },
  { key: 'amountAfterDisc', label: 'Amount (A Disc)' }
];

/** A4 GST invoice column options – default all on */
export const A4_GST_COLUMN_OPTIONS: { key: A4GstBillColumnKey; label: string }[] = [
  { key: 'sno', label: 'S.No' },
  { key: 'particulars', label: 'Particulars' },
  { key: 'hsn', label: 'HSN' },
  { key: 'mrp', label: 'MRP' },
  { key: 'qty', label: 'Qty' },
  { key: 'rateBeforeDisc', label: 'Rate (B Disc)' },
  { key: 'discount', label: 'Discount' },
  { key: 'rateAfterDisc', label: 'Rate (A Disc)' },
  { key: 'rate', label: 'Rate' },
  { key: 'taxValue', label: 'Tax Value' },
  { key: 'gstRate', label: 'GST%' },
  { key: 'cgst', label: 'CGST' },
  { key: 'sgst', label: 'SGST' },
  { key: 'amountBeforeDisc', label: 'Amount (B Disc)' },
  { key: 'amountAfterDisc', label: 'Amount (A Disc)' }
];

/** A4 Non-GST estimate column options – default all on */
export const A4_NON_GST_COLUMN_OPTIONS: { key: A4NonGstBillColumnKey; label: string }[] = [
  { key: 'sno', label: 'S.No' },
  { key: 'particulars', label: 'Particulars' },
  { key: 'mrp', label: 'MRP' },
  { key: 'qty', label: 'Qty' },
  { key: 'rateBeforeDisc', label: 'Rate (B Disc)' },
  { key: 'discount', label: 'Discount' },
  { key: 'rateAfterDisc', label: 'Rate (A Disc)' },
  { key: 'amountBeforeDisc', label: 'Amount (B Disc)' },
  { key: 'amountAfterDisc', label: 'Amount (A Disc)' }
];

@Component({
  selector: 'app-printer-settings',
  templateUrl: './printer-settings.component.html',
  styleUrls: ['./printer-settings.component.scss']
})
export class PrinterSettingsComponent implements OnInit {
  showModal = false;
  thermalEnabled = true;
  a4Enabled = true;
  a5Enabled = true;
  paperSize: PaperSize = '2inch';
  connectionType: ConnectionType = 'bluetooth';
  labelProtocol: LabelProtocol = 'escpos';
  labelFeedOffsetMm = 0;
  readonly labelWidthMm = PRODUCT_LABEL_WIDTH_MM;
  readonly labelHeightMm = PRODUCT_LABEL_HEIGHT_MM;
  readonly labelGapMm = PRODUCT_LABEL_GAP_MM;
  readonly labelPitchDots = PRODUCT_LABEL_PITCH_DOTS;
  a5BillColumns: A5BillColumns = { sno: true, particulars: true, mrp: true, rateBeforeDisc: true, discount: true, qty: true, amountAfterDisc: true };
  a4GstBillColumns: A4GstBillColumns = {} as A4GstBillColumns;
  a4NonGstBillColumns: A4NonGstBillColumns = {} as A4NonGstBillColumns;
  readonly a5ColumnOptions = A5_COLUMN_OPTIONS;
  readonly a4GstColumnOptions = A4_GST_COLUMN_OPTIONS;
  readonly a4NonGstColumnOptions = A4_NON_GST_COLUMN_OPTIONS;
  isConnecting = false;

  constructor(private printerService: PrinterService) {}

  ngOnInit(): void {
    const settings = this.printerService.getSettings();
    this.thermalEnabled = settings.thermalEnabled !== false;
    this.a4Enabled = settings.a4Enabled !== false;
    this.a5Enabled = settings.a5Enabled !== false;
    this.paperSize = settings.paperSize;
    this.connectionType = settings.connectionType;
    this.labelProtocol = settings.labelProtocol === 'tspl' ? 'tspl' : 'escpos';
    this.labelFeedOffsetMm = settings.labelFeedOffsetMm ?? 0;
    this.a5BillColumns = this.printerService.getA5BillColumns();
    this.a4GstBillColumns = this.printerService.getA4GstBillColumns();
    this.a4NonGstBillColumns = this.printerService.getA4NonGstBillColumns();
  }

  openModal(): void {
    const settings = this.printerService.getSettings();
    this.thermalEnabled = settings.thermalEnabled !== false;
    this.a4Enabled = settings.a4Enabled !== false;
    this.a5Enabled = settings.a5Enabled !== false;
    this.paperSize = settings.paperSize;
    this.connectionType = settings.connectionType;
    this.labelProtocol = settings.labelProtocol === 'tspl' ? 'tspl' : 'escpos';
    this.labelFeedOffsetMm = settings.labelFeedOffsetMm ?? 0;
    this.a5BillColumns = this.printerService.getA5BillColumns();
    this.a4GstBillColumns = this.printerService.getA4GstBillColumns();
    this.a4NonGstBillColumns = this.printerService.getA4NonGstBillColumns();
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  saveSettings(): void {
    const settings: PrinterSettings = {
      thermalEnabled: this.thermalEnabled,
      a4Enabled: this.a4Enabled,
      a5Enabled: this.a5Enabled,
      paperSize: this.paperSize,
      connectionType: this.connectionType,
      labelProtocol: this.labelProtocol,
      labelFeedOffsetMm: this.labelFeedOffsetMm,
      a5BillColumns: this.a5BillColumns,
      a4GstBillColumns: this.a4GstBillColumns,
      a4NonGstBillColumns: this.a4NonGstBillColumns
    };
    this.printerService.saveSettings(settings);
    this.closeModal();
  }

  onThermalEnabledToggle(event: Event): void {
    this.thermalEnabled = (event.target as HTMLInputElement).checked;
  }

  onA4EnabledToggle(event: Event): void {
    this.a4Enabled = (event.target as HTMLInputElement).checked;
  }

  onA5EnabledToggle(event: Event): void {
    this.a5Enabled = (event.target as HTMLInputElement).checked;
  }

  toggleA5Column(key: A5BillColumnKey): void {
    this.a5BillColumns = { ...this.a5BillColumns, [key]: !this.a5BillColumns[key] };
  }

  toggleA4GstColumn(key: A4GstBillColumnKey): void {
    this.a4GstBillColumns = { ...this.a4GstBillColumns, [key]: !this.a4GstBillColumns[key] };
  }

  toggleA4NonGstColumn(key: A4NonGstBillColumnKey): void {
    this.a4NonGstBillColumns = { ...this.a4NonGstBillColumns, [key]: !this.a4NonGstBillColumns[key] };
  }

  async testConnection(): Promise<void> {
    this.isConnecting = true;
    try {
      let connected = false;
      if (this.connectionType === 'bluetooth') {
        connected = await this.printerService.connectBluetoothPrinter();
      } else {
        connected = await this.printerService.connectUsbOrSerialPrinter();
      }
      
      // Auto-save settings after successful connection
      if (connected) {
        console.log('[PrinterSettings] Connection successful, auto-saving settings...');
        const settings: PrinterSettings = {
          thermalEnabled: this.thermalEnabled,
          a4Enabled: this.a4Enabled,
          a5Enabled: this.a5Enabled,
          paperSize: this.paperSize,
          connectionType: this.connectionType,
          labelProtocol: this.labelProtocol,
          labelFeedOffsetMm: this.labelFeedOffsetMm,
          a5BillColumns: this.a5BillColumns,
          a4GstBillColumns: this.a4GstBillColumns,
          a4NonGstBillColumns: this.a4NonGstBillColumns
        };
        this.printerService.saveSettings(settings);
        console.log('[PrinterSettings] Settings saved:', settings);
      }
    } finally {
      this.isConnecting = false;
    }
  }

  async selectNewPort(): Promise<void> {
    this.isConnecting = true;
    try {
      await this.printerService.selectNewSerialPort();
    } finally {
      this.isConnecting = false;
    }
  }

  clearSavedConnection(): void {
    if (confirm('Clear saved printer connection? You will need to select your printer again next time.')) {
      this.printerService.clearSerialConfig();
    }
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.showModal) {
      this.closeModal();
    }
  }
}

